/* eslint-disable react/no-multi-comp */
import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { DESKTOP_COLORS } from 'src/consts';
import { useResponsive } from 'src/hooks/useResponsive';
import { InfiniteLoader } from 'ui/InfiniteLoader';
import { Location, LocationsData } from './model/Location';
import { LocationCard } from './LocationCard';

const PREFIX = 'LocationsList';

const classes = {
    locationSectionTitle: `${PREFIX}locationSectionTitle`
};

const StyledBox = styled(Box, {
    shouldForwardProp: prop => prop !== 'isDesktop'
})<{ isDesktop: boolean }>(({ theme, isDesktop }) => ({
    padding: theme.spacing(1.5),
    width: '100%',
    flex: 1,
    [`& .${classes.locationSectionTitle}`]: {
        fontFamily: `TenantFont, ${theme.typography.fontFamily}`,
        textAlign: isDesktop ? 'center' : 'left',
        ...(isDesktop
            ? {
                  color: DESKTOP_COLORS.TEXT_PRIMARY
              }
            : {})
    }
}));

interface LocationsListProps {
    list: Location[];
    loadMore: () => any;
    isLoading: boolean;
    hasNext: boolean;
    hasGeoLocation: boolean;
}

export const LocationsList: React.FC<React.PropsWithChildren<LocationsListProps>> = props => {
    const { isDesktop } = useResponsive();

    const { t } = useTranslation();
    const { list, hasNext, loadMore, isLoading, hasGeoLocation } = props;

    const { closest, others } = React.useMemo(
        () =>
            list.reduce<{ closest: Location | null; others: Location[] }>(
                (acc, location) => {
                    // Compare closest location with previous one
                    if (!acc.closest && hasGeoLocation) {
                        acc.closest = location;
                    } else if (!!acc.closest && acc.closest.distance && location.distance) {
                        if (location.distance < acc.closest.distance) {
                            acc.closest = location;
                        }
                    }
                    if (acc.closest?._id !== location._id || !acc.closest) {
                        acc.others.push(location);
                    }

                    return acc;
                },
                { closest: null, others: [] }
            ),
        [list, hasGeoLocation]
    );
    const renderRow = React.useCallback(
        (location: Location, index: number, arr: Location[]) => (
            <Box key={location._id} marginBottom={index < arr.length - 1 && isDesktop ? 3.25 : 0}>
                <LocationCard location={location} />
            </Box>
        ),
        [isDesktop]
    );
    const handleListGetter = React.useCallback((data: LocationsData) => data.locations, []);
    return (
        <StyledBox isDesktop={isDesktop}>
            {!!closest && hasGeoLocation && (
                <>
                    <Typography variant="h6" className={classes.locationSectionTitle}>
                        {t('LOCATIONS_CLOSEST')}
                    </Typography>
                    <LocationCard location={closest} />
                </>
            )}
            {others.length > 0 && hasGeoLocation && (
                <Box marginTop={2}>
                    <Typography variant="h6" className={classes.locationSectionTitle}>
                        {t('LOCATIONS_OTHERS')}
                    </Typography>
                </Box>
            )}
            <InfiniteLoader<Location, LocationsData>
                list={others}
                loadMore={loadMore}
                dataListGetter={handleListGetter}
                isLoading={isLoading}
                hasNext={hasNext}
            >
                {(locations: Location[]) => locations.map(renderRow)}
            </InfiniteLoader>
        </StyledBox>
    );
};
